import { useState } from "react";
import { MapPin, Phone, Mail, Clock, ShieldCheck, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import Logo, { CitekMark } from "@/components/Logo";

const TRAVAUX_OPTIONS = [
  "Gros œuvre & maçonnerie",
  "Rénovation complète",
  "Aménagement tertiaire / ERP",
  "Extension / surélévation",
  "Autre projet T.C.E",
];

const inputCls =
  "w-full bg-[#F6F7F4] border border-black/10 rounded-md px-4 py-3.5 text-base text-zinc-900 placeholder-zinc-400 outline-none focus:border-citek-greenDark transition-colors";

export function Contact({ contact }) {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", type_travaux: TRAVAUX_OPTIONS[0], budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Votre demande a bien été envoyée. Nous vous répondrons rapidement.", { data: { testid: "contact-success-toast" } });
      setForm({ nom: "", telephone: "", email: "", type_travaux: TRAVAUX_OPTIONS[0], budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  const infos = [
    { icon: MapPin, label: "Adresse", value: contact.address, testid: "contact-info-address" },
    { icon: Phone, label: "Téléphone", value: contact.phone, href: `tel:${contact.phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}`, testid: "contact-info-email" },
    { icon: Clock, label: "Horaires", value: contact.hours, testid: "contact-info-hours" },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#EBECE8] scroll-mt-20" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark" data-testid="contact-kicker">
            {contact.kicker}
          </p>
          <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-zinc-900 text-3xl md:text-5xl leading-[1.05] max-w-lg" data-testid="contact-title">
            {contact.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-zinc-600 max-w-md" data-testid="contact-subtitle">
            {contact.subtitle}
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {infos.map((info, i) => {
              const Icon = info.icon;
              const body = (
                <>
                  <Icon size={18} className="text-citek-greenDark shrink-0 mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-tech text-[11px] uppercase tracking-[0.2em] text-zinc-500">{info.label}</span>
                    <span className="block mt-1 text-base text-zinc-800 break-words">{info.value}</span>
                  </span>
                </>
              );
              return info.href ? (
                <a key={i} href={info.href} data-testid={info.testid} className="flex gap-3 items-start border border-black/10 rounded-lg bg-white p-4 hover:border-citek-greenDark/50 hover:shadow-md transition-all">
                  {body}
                </a>
              ) : (
                <div key={i} data-testid={info.testid} className="flex gap-3 items-start border border-black/10 rounded-lg bg-white p-4">
                  {body}
                </div>
              );
            })}
          </div>

          {contact.guarantee && (
            <p className="mt-8 flex items-center gap-2 font-tech text-xs uppercase tracking-[0.18em] text-zinc-500" data-testid="contact-guarantee">
              <ShieldCheck size={16} className="text-citek-greenDark" />
              {contact.guarantee}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-black/10 rounded-xl bg-white p-6 md:p-8 space-y-4 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-nom">Nom / Société *</label>
                <input id="f-nom" required value={form.nom} onChange={set("nom")} data-testid="contact-input-nom" className={inputCls} placeholder="Votre nom" />
              </div>
              <div>
                <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-tel">Téléphone</label>
                <input id="f-tel" type="tel" value={form.telephone} onChange={set("telephone")} data-testid="contact-input-telephone" className={inputCls} placeholder="06 00 00 00 00" />
              </div>
            </div>
            <div>
              <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-email">Email *</label>
              <input id="f-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-input-email" className={inputCls} placeholder="vous@exemple.fr" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-type">Type d'intervention</label>
                <select id="f-type" value={form.type_travaux} onChange={set("type_travaux")} data-testid="contact-select-type" className={inputCls}>
                  {TRAVAUX_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-budget">Budget estimatif (€)</label>
                <input id="f-budget" value={form.budget} onChange={set("budget")} data-testid="contact-input-budget" className={inputCls} placeholder="50 000" />
              </div>
            </div>
            <div>
              <label className="block font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2" htmlFor="f-message">Votre projet</label>
              <textarea id="f-message" rows={4} value={form.message} onChange={set("message")} data-testid="contact-input-message" className={inputCls} placeholder="Décrivez votre projet et sa localisation…" />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-citek-green text-black font-semibold text-base px-6 py-4 rounded-md hover:bg-citek-greenBright transition-colors disabled:opacity-60"
            >
              <Send size={17} />
              {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer({ footer }) {
  return (
    <footer className="relative bg-[#EBECE8] border-t border-black/10 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
          <div>
            <Logo />
            <p className="mt-4 text-base text-zinc-600 max-w-xs" data-testid="footer-tagline">{footer.tagline}</p>
          </div>
          <div className="font-tech text-xs uppercase tracking-[0.16em] text-zinc-500 leading-loose max-w-md" data-testid="footer-legal">
            {footer.legal}
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-black/10 pt-6">
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-zinc-500" data-testid="footer-copyright">
            © {new Date().getFullYear()} CITEK Entreprise S.A.S — Tous droits réservés
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            <a href="/cgv" data-testid="footer-cgv-link" className="font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-citek-greenDark transition-colors">
              Conditions Générales de Vente
            </a>
            <a href="/admin" data-testid="footer-admin-link" className="flex items-center gap-2 font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-citek-greenDark transition-colors">
              <CitekMark size={16} />
              Espace administrateur
            </a>
          </div>
        </div>
      </div>

      <div className="pointer-events-none select-none relative -mb-[3vw] opacity-60" aria-hidden="true">
        <p className="font-display font-extrabold uppercase text-center leading-none text-stroke text-[19vw] tracking-tight">
          CITEK
        </p>
      </div>
    </footer>
  );
}
